import { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';

export const NotificationPrompt = () => {
  const { permission, requestPermission, loading, isSupported } = useNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isSupported || permission === 'granted' || permission === 'denied') return;

    // Show after a delay, and only if not recently dismissed
    const dismissedAt = localStorage.getItem('notif-prompt-dismissed');
    if (dismissedAt) {
      const days = (Date.now() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
      if (days < 14) return;
    }

    const timer = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(timer);
  }, [isSupported, permission]);

  const handleEnable = async () => {
    const success = await requestPermission();
    if (success) setShow(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
    localStorage.setItem('notif-prompt-dismissed', new Date().toISOString());
  };

  if (!show || dismissed || permission !== 'default') return null;

  return (
    <div
      className="lg:hidden fixed bottom-20 left-3 right-3 z-50 bg-blue-600 text-white rounded-xl p-3.5 shadow-2xl border border-blue-500 animate-in slide-in-from-bottom-4"
      data-testid="notification-prompt"
    >
      <button onClick={handleDismiss} className="absolute top-2 right-2 text-blue-200 hover:text-white" data-testid="notif-prompt-dismiss">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Enable Notifications</p>
          <p className="text-xs text-blue-100">Get alerts for games, invoices & team messages</p>
        </div>
        <Button
          size="sm"
          onClick={handleEnable}
          disabled={loading}
          className="bg-white text-blue-600 hover:bg-blue-50 flex-shrink-0"
          data-testid="notif-prompt-enable"
        >
          {loading ? 'Enabling...' : 'Enable'}
        </Button>
      </div>
    </div>
  );
};
